import { createFileRoute, Link } from "@tanstack/react-router";
import { MarketingShell } from "@/components/site/marketing-shell";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";

export const Route = createFileRoute("/plans")({
  head: () => ({
    meta: [
      { title: "Investment Plans — CryptoBull Pro" },
      { name: "description", content: "Four tiered crypto investment plans: Starter, Silver, Gold and VIP. ROI 1.5%–5% daily." },
      { property: "og:title", content: "CryptoBull Pro — Investment Plans" },
      { property: "og:description", content: "Tiered investment plans with daily ROI." },
    ],
  }),
  component: Plans,
});

function Plans() {
  const { data: plans = [] } = useQuery({
    queryKey: ["plans"],
    queryFn: async () => {
      const { data, error } = await supabase.from("plans").select("*").eq("is_active", true).order("sort_order");
      if (error) throw error;
      return data;
    },
  });

  return (
    <MarketingShell>
      <section className="mx-auto max-w-7xl px-4 py-20">
        <div className="text-center max-w-2xl mx-auto">
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight">Investment plans</h1>
          <p className="mt-4 text-muted-foreground">Pick a tier that matches your capital. Earnings accrue daily for the full duration of the plan.</p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {plans.map((p, idx) => {
            const featured = idx === 2;
            return (
              <div key={p.id} className={`glass rounded-2xl p-6 relative ${featured ? "ring-2 ring-primary/50" : ""}`}>
                {featured && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-semibold">Most popular</div>}
                <div className="text-sm uppercase tracking-widest text-muted-foreground">{p.name}</div>
                <div className="mt-3 font-mono">
                  <span className="text-4xl font-semibold">{Number(p.daily_roi)}%</span>
                  <span className="text-sm text-muted-foreground"> / day</span>
                </div>
                <div className="mt-1 text-xs text-muted-foreground font-mono">for {p.duration_days} days</div>
                <ul className="mt-6 space-y-2 text-sm">
                  {[
                    `Min $${Number(p.min_amount).toLocaleString()}`,
                    `Max $${Number(p.max_amount).toLocaleString()}`,
                    `${Number(p.referral_bonus)}% referral bonus`,
                    `${(Number(p.daily_roi) * p.duration_days).toFixed(0)}% total ROI`,
                    "24/7 support",
                  ].map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className="h-4 w-4 mt-0.5 text-primary shrink-0" /> <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Button asChild className="w-full mt-6" variant={featured ? "default" : "outline"}>
                  <Link to="/deposit" search={{ plan: p.slug }}>Invest now</Link>
                </Button>
              </div>
            );
          })}
        </div>
      </section>
    </MarketingShell>
  );
}